import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { getLocations } from '../../api/master'
import Spinner from '../../components/Spinner'
import ErrorBanner from '../../components/ErrorBanner'
import { StatusBadge } from '../../components/PageParts'
import { useSession } from '../../session'
import { MasterTabs, placeLine } from './masterShared'

export const taxOn = (l) => !['0', 'false', 'no', 'off'].includes(String(l?.tax_enabled ?? '1').trim().toLowerCase())
export const taxText = (l) => (taxOn(l) ? `${l.tax_name || 'Sales Tax'} ${Number(l.tax_rate || 0).toFixed(3).replace(/\.?0+$/, '')}%` : 'No sales tax')

// Locations: your stores and warehouses, each with its own sales tax.
export default function LocationList() {
  const { data: rows = [], isLoading, error } = useQuery({ queryKey: ['locations'], queryFn: getLocations })
  // The plan includes some locations; more are added by ASYS.
  const { user } = useSession()
  const limit = user?.location_limit
  const full = limit != null && rows.length >= limit
  const sorted = [...rows].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
  return (
    <div className="ci-page">
      <MasterTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Locations</h1>
            <div className="ci-title-meta">Your stores and warehouses. Each has its own stock and sales tax.{limit != null && ` ${rows.length} of ${limit} on your plan.`}</div>
          </div>
        </div>
        <div className="ci-header-actions">
          {full
            ? <Link className="ci-btn ci-btn-ghost" to="/settings/plan" title="Extra locations are $25/month each."><Plus size={15} />Add a location to your plan</Link>
            : <Link className="ci-btn ci-btn-primary" to="/master/locations/new"><Plus size={15} />New location</Link>}
        </div>
      </div>

      {error && <ErrorBanner message="Couldn’t load the locations." />}

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table fit-sm">
            <thead><tr><th>Location</th><th className="hide-sm">Type</th><th className="hide-sm">Address</th><th>Sales tax</th></tr></thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan="4"><Spinner label="Loading locations…" /></td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan="4"><div className="ci-empty">No locations yet. <Link to="/master/locations/new">Add your store</Link>.</div></td></tr>
              ) : sorted.map((l) => (
                <tr key={l.location_id}>
                  <td>
                    <Link className="ci-strong mf-row-link" to={`/master/locations/${l.location_id}`}>{l.name}</Link>
                    <span className="cell-sub">{placeLine(l) || l.country || ''}</span>
                  </td>
                  <td className="hide-sm"><StatusBadge label={l.location_type || 'Store'} tone="muted" /></td>
                  <td className="hide-sm ci-muted-cell">{[l.address, l.postal_code].filter(Boolean).join(', ') || '–'}</td>
                  <td>{taxOn(l) ? taxText(l) : <span className="ci-muted-cell">None</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ci-pagination"><span className="ci-page-info">{sorted.length} {sorted.length === 1 ? 'location' : 'locations'}</span></div>
      </div>
    </div>
  )
}
